import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import postsServices from "../../../services/api/service/postsService";
import RemoveIcon from "./../../../assets/images/icons/remove-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  removeFormDataKeys,
  setHorizontalSteps,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import ModalPopup from "../ModalPopup";
import { PrimaryButton } from "../Button";
import { RootState, store } from "../../../services/redux/store";

const EntranceExamDetailsSection = ({
  fields,
  sectionPrefix,
  values,
  setFieldValue,
  renderField,
  horizontalSteps,
  activeVerticalIndex,
  activeHorizontalIndex,
  formikRef,
  dropdownData,
  allFields
}: any) => {
  const dispatch = useDispatch();
  const radioField = fields.find((f: any) => f.type === "radio");
  const coreExamFields = allFields && allFields?.length > 0 && allFields.filter((f: any) => f.name.startsWith("core")) || [];
  const showFields = radioField && values[radioField.name] === "Yes";
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<number[] | null>(
    null
  );
  const addExamInProgressRef = useRef(false);
  const { trackingId } = useSelector((state: RootState) => state.stepper);

  const clearRemovedFormikKeys = (fieldNamesToRemove: string[]) => {
    if (!formikRef?.current) return [] as string[];

    const namesToDelete = new Set<string>();
    const suffixes = new Set<string>();

    fieldNamesToRemove.forEach((name) => {
      namesToDelete.add(name);
      namesToDelete.add(name.replace("ExamID", "ExamId"));
      namesToDelete.add(name.replace("ExamId", "ExamID"));

      const match = name.match(/_(\d+)$/);
      if (match) suffixes.add(match[1]);
    });

    suffixes.forEach((suffix) => {
      if (sectionPrefix === "core") {
        [
          `coreExamID_${suffix}`,
          `coreExamId_${suffix}`,
          `coreExamName_${suffix}`,
          `coreScoreReceived_${suffix}`,
          `coreExamScore_${suffix}`,
          `coreTotalMarks_${suffix}`,
        ].forEach((key) => namesToDelete.add(key));
      } else {
        [
          `languageExamID_${suffix}`,
          `languageExamId_${suffix}`,
          `languageExamName_${suffix}`,
          `languageScoreReceived_${suffix}`,
          `languageExamScore_${suffix}`,
          `languageTotalMarks_${suffix}`,
        ].forEach((key) => namesToDelete.add(key));
      }
    });

    const nextValues = { ...(formikRef.current.values ?? {}) };
    namesToDelete.forEach((name) => {
      delete nextValues[name];
      setFieldValue(name, undefined, false);
    });

    if (typeof formikRef.current.setValues === "function") {
      formikRef.current.setValues(nextValues, false);
    }

    return Array.from(namesToDelete);
  };

  const getNextDynamicSuffix = () => {
    const examKey = sectionPrefix === "core" ? "coreExamID" : "languageExamID";
    const suffixes = fields
      .map((field: any) => {
        const regex = new RegExp(`^${examKey}_(\\d+)$`);
        const match = field?.name?.match(regex);
        return match ? Number(match[1]) : null;
      })
      .filter((value: number | null): value is number =>
        typeof value === "number" && !Number.isNaN(value)
      );

    const maxSuffix = suffixes.length ? Math.max(...suffixes) : 0;
    return maxSuffix + 1;
  };

  // Section title logic
  const sectionTitle =
    sectionPrefix === "core"
      ? "Core Entrance Exam Details"
      : sectionPrefix === "language"
      ? "Language Entrance Exam Details"
      : "";

  const addMoreBtnTitle =
    sectionPrefix === "core"
      ? "Add Core Entrance Exam"
      : sectionPrefix === "language"
      ? "Add Language Entrance Exam"
      : "";

  const coreExamOptions =
    fields.find((f: any) => f.name?.includes("coreExamName"))?.options
      ?.options ?? [];

  const coreScoreOptions =
    fields.find((f: any) => f.name?.includes("coreScoreReceived"))?.options
      ?.options ?? [];

  const languageTypeOptions =
    fields.find((f: any) => f.name?.includes("languageExamName"))?.options
      ?.options ?? [];

  const languageScoreOptions =
    fields.find((f: any) => f.name?.includes("languageScoreReceived"))?.options
      ?.options ?? [];

  // Get the current vertical step object
  const currentVerticalStep =
    horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[
      activeVerticalIndex
    ];

  const coreExamValues = Object.keys(values).filter((key) =>
    key.includes("coreExamName")
  );
  const languageExamValues = Object.keys(values).filter((key) =>
    key.includes("languageExamName")
  );

  const isLanguageExamLimitReached = () => {
    const languageExamTypeCount =
      dropdownData?.languageExamTypes?.length ?? languageTypeOptions?.length ?? 0;

    return (
      languageExamTypeCount > 0 &&
      fields.filter((field: any) =>
        /^languageExamName(_\d+)?$/.test(field?.name ?? "")
      ).length >= languageExamTypeCount
    );
  };

  const isCoreExamLimitReached = () => {
    const coreExamTypeCount =
    dropdownData?.entranceExamTypes?.length ?? coreExamOptions?.length ?? 0;
    
     return (
      coreExamTypeCount > 0 &&
      coreExamFields?.filter((field: any) =>
        /^coreExamName(_\d+)?$/.test(field?.name ?? "")
      ).length >= coreExamTypeCount
    );
  };

  const disabledAddMoreBtn: boolean =
    sectionPrefix === "core"
      ? coreExamValues?.length >= dropdownData?.entranceExamTypes?.length
      : languageExamValues?.length >= dropdownData?.languageExamTypes?.length;

  // Get the vertical step id
  const verticalStepId = currentVerticalStep?.id;

  // console.log(dropdownData?.entranceExamTypes?.length, coreExamOptions?.length, "dropdownData")

  /**
   * Adds a new set of exam fields to the form and updates the backend.
   *
   * - Sets loading state while processing.
   * - Clones and transforms the `examFields` array, appending a unique suffix to each field's `name` property.
   * - Sends the transformed fields to the backend API to store user fields.
   * - On successful response, initializes Formik values for the new fields and updates the Redux store with the new entry.
   * - Handles errors and resets loading state.
   *
   * @async
   * @function
   * @returns {Promise<void>}
   */
  const addExamFields = async () => {
    if (
      addExamInProgressRef.current ||
      (((sectionPrefix === "language" && isLanguageExamLimitReached()) || (sectionPrefix === "core" && isCoreExamLimitReached())))
    )
      return;

    addExamInProgressRef.current = true;
    setIsLoading(true);
    try {
      const nextNum = getNextDynamicSuffix();

      // clone existing fields but append suffix
      /**
       * Transforms the `examFields` array by mapping each field object to a new object with updated properties.
       * - Copies all properties from the original field.
       * - Sets the `label` property to a string version of the original label.
       * - Appends `nextNum` as a suffix to the `name` property to ensure uniqueness (useful for Formik forms).
       * - Preserves the `options` property for dropdown fields.
       *
    
       * @param nextNum - Suffix to append to each field's `name` property.
       * @returns A new array of field objects with updated `label`, `name`, and preserved `options`.
       */
      // Example for language section (update for core similarly)
      const apiFields =
        sectionPrefix === "language"
          ? [
              {
                label: "Exam ID",
                name: `languageExamID_${nextNum}`,
                type: "hidden",
                required: true,
                options: null,
              },
              {
                label: "Exam Name",
                name: `languageExamName_${nextNum}`,
                type: "dropdown",
                required: true,
                options: {
                  options: languageTypeOptions,
                },
              },
              {
                label: "Received Your Results?",
                name: `languageScoreReceived_${nextNum}`,
                type: "dropdown",
                required: true,
                options: {
                  options: languageScoreOptions,
                },
              },
              {
                label: "Score Expected On",
                name: `languageScoreExpectedOn_${nextNum}`,
                type: "date",
                required: false,
                options: null,
              },
              {
                label: "Marks Obtained",
                name: `languageExamScore_${nextNum}`,
                type: "number",
                required: true,
                options: null,
              },
              {
                label: "Total Marks",
                name: `languageTotalMarks_${nextNum}`,
                type: "number",
                required: true,
                options: null,
              },
            ]
          : [
              // Example for core section
              {
                label: "Exam ID",
                name: `coreExamID_${nextNum}`,
                type: "hidden",
                required: true,
                options: {
                  options: coreExamOptions,
                },
              },
              {
                label: "Exam Name",
                name: `coreExamName_${nextNum}`,
                type: "dropdown",
                required: true,
                options: {
                  options: coreExamOptions,
                },
              },
              {
                label: "Received Your Results?",
                name: `coreScoreReceived_${nextNum}`,
                type: "dropdown",
                required: true,
                options: {
                  options: coreScoreOptions,
                },
              },
              {
                label: "Score Expected On",
                name: `coreScoreExpectedOn_${nextNum}`,
                type: "date",
                required: false,
                options: null,
              },
              {
                label: "Marks Obtained",
                name: `coreExamScore_${nextNum}`,
                type: "number",
                required: true,
                options: null,
              },
              {
                label: "Total Marks",
                name: `coreTotalMarks_${nextNum}`,
                type: "number",
                required: true,
                options: null,
              },
            ];

      // Call the API
      const resp = await postsServices.addPost(
        "api/v1/product/store-user-fields",
        {
          vertical_step_id: verticalStepId,
          trackingId: trackingId,
          fields: apiFields,
        }
      );

      if (resp.data?.status === "success") {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          name: f.name, // backend returns correct name
        }));

        // --- PUSH new fields into the correct vertical step (id: 9, Loan Details) in applicant_loan horizontal step in Redux state ---
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];

          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );

          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Instead of hardcoding id === 9, use the current vertical step (activeVerticalIndex)
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = [...targetStep.fields, ...newFields];
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update using setHorizontalSteps action creator
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // initialize Formik values
        newFields.forEach((f: any) => {
          setFieldValue(f.name, "");
        });
      }
    } catch (err) {
      console.error("Add entry failed", err);
    } finally {
      addExamInProgressRef.current = false;
      setIsLoading(false);
    }
  };

  /**
   * Removes an entrance exam entry at the specified index.
   *
   * This function performs the following steps:
   * 1. Retrieves the entry at the given index from the `entries` array.
   * 2. Sends a request to delete the associated form field IDs from the backend.
   * 3. If the deletion is successful, clears the corresponding Formik field values.
   * 4. Removes the entry from the local state using Redux dispatch.
   *
   * @param idx - The index of the entrance exam entry to remove.
   * @returns A promise that resolves when the removal process is complete.
   */
  const removeExamFields = async (fieldIds: number[]) => {
    try {
      // First, find the fields to get their names before removing
      const state = store.getState();
      let fieldNamesToRemove: string[] = [];

      if (
        state &&
        state.stepper &&
        Array.isArray(state.stepper.horizontalSteps)
      ) {
        const horizontalSteps = [...state.stepper.horizontalSteps];
        const applicantLoanStepIdx = horizontalSteps.findIndex(
          (step: any) => step.stepKey === "applicant_loan"
        );
        if (applicantLoanStepIdx !== -1) {
          const applicantLoanStep = horizontalSteps[applicantLoanStepIdx];
          const verticalSteps = applicantLoanStep.verticalSteps;
          const targetVerticalIdx = activeVerticalIndex;
          if (
            typeof targetVerticalIdx === "number" &&
            targetVerticalIdx >= 0 &&
            targetVerticalIdx < verticalSteps.length
          ) {
            const targetStep = verticalSteps[targetVerticalIdx];
            // Extract field names for the fields being removed
            fieldNamesToRemove = targetStep.fields
              .filter((field: any) => fieldIds?.includes(field.id))
              .map((field: any) => field.name);
          }
        }
      }

      const resp = await postsServices.addPost(
        "api/v1/product/delete-user-fields",
        {
          form_field_ids: fieldIds,
          trackingId: trackingId,
        }
      );

      if (resp.data?.status === "success") {
        // Remove from Redux: Remove fields from horizontalSteps (vertical step id: 9 in applicant_loan)
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];
          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );
          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Use the current vertical step (activeVerticalIndex) instead of hardcoded id === 9
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = targetStep.fields.filter(
                (field: any) => !fieldIds?.includes(field.id)
              );
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // Remove deleted fields from Formik values object and persisted Redux formData
        const deletedKeys = clearRemovedFormikKeys(fieldNamesToRemove);
        if (deletedKeys.length > 0) {
          dispatch(removeFormDataKeys(deletedKeys));
        }
      }
    } catch (err) {
      console.error("Delete entry failed", err);
    }
  };

  // Handler for remove button click
  const handleRemoveClick = (ids: number[]) => {
    setPendingRemoveIds(ids);
    setConfirmOpen(true);
  };

  // Handler for confirm in modal
  const handleConfirmRemove = () => {
    if (pendingRemoveIds !== null) {
      removeExamFields(pendingRemoveIds);
    }
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  // Handler for cancel in modal
  const handleCancelRemove = () => {
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  useEffect(() => {
    if (radioField && !values[radioField.name])
      setFieldValue(radioField.name, "Yes");
  }, [radioField, setFieldValue, values]);

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {isXsOrSm && showFields && (
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "15px" }}>
            {sectionTitle}
          </Typography>
        </Grid>
      )}

      {/* radio field */}
      <Grid item xs={12}>
        {renderField && radioField && renderField(radioField)}
      </Grid>

      {/* Section Title */}
      {!isXsOrSm && (
        <Grid container item alignItems={"center"}>
          {showFields && (
            <Grid item xs={6}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: "15px" }}
              >
                {sectionTitle}
              </Typography>
            </Grid>
          )}

          <Grid item xs={6} textAlign={"right"}>
            {/* add more button */}
            {showFields && (
              <Button
                sx={{
                  textTransform: "none",
                  fontFamily: "Jost, sans-serif",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#33c2c4",
                  borderColor: "#33c2c4",
                  "&:hover": {
                    backgroundColor: "transparent",
                    borderColor: "#33c2c4",
                    color: "#33c2c4",
                  },
                }}
                onClick={addExamFields}
                disabled={isLoading || disabledAddMoreBtn}
              >
                <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
                Add More
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      {/* dynamically added entries */}
      {showFields && (
        <>
          {/* Static core/language fields (first row) with conditional required logic */}
          {(() => {
            const staticFields = fields.filter(
              (f: any) => !f.isRemoved && f.type !== "radio"
            );
            const staticCoreScoreReceived =
              formikRef.current?.values?.coreScoreReceived;
            const staticLanguageScoreReceived =
              formikRef.current?.values?.languageScoreReceived;
            // For static fields, check Score Received value and set required accordingly
            const updatedFields = staticFields.map((field: any) => {
              // Only apply to core fields
              if (
                field.name === "coreExamScore" ||
                field.name === "coreTotalMarks"
              ) {
                const scoreReceived =
                  formikRef.current?.values?.coreScoreReceived;
                if (scoreReceived === "No") {
                  return { ...field, required: false };
                }
                if (scoreReceived === "Yes") {
                  return { ...field, required: true };
                }
              }
              // Make core scoreExpectedOn required when scoreReceived is No
              if (field.name === "coreScoreExpectedOn") {
                const scoreReceived =
                  formikRef.current?.values?.coreScoreReceived;
                return { ...field, required: scoreReceived === "No" };
              }
              // Only apply to language fields
              if (
                field.name === "languageExamScore" ||
                field.name === "languageTotalMarks"
              ) {
                const scoreReceived =
                  formikRef.current?.values?.languageScoreReceived;
                if (scoreReceived === "No") {
                  return { ...field, required: false };
                }
                if (scoreReceived === "Yes") {
                  return { ...field, required: true };
                }
              }
              // Make language scoreExpectedOn required when scoreReceived is No
              if (field.name === "languageScoreExpectedOn") {
                const scoreReceived =
                  formikRef.current?.values?.languageScoreReceived;
                return { ...field, required: scoreReceived === "No" };
              }
              return field;
            });
            console.log(updatedFields,"updatedFields")
            if (staticFields.length > 0) {
              return (
                <Grid container item spacing={2}>
                  {updatedFields.map((f: any) => {
                    const hideCoreExpectedDate =
                      f.name === "coreScoreExpectedOn" &&
                      staticCoreScoreReceived !== "No";
                    const hideCoreMarks =
                      (f.name === "coreExamScore" ||
                        f.name === "coreTotalMarks") &&
                      staticCoreScoreReceived === "No";
                    const hideLanguageExpectedDate =
                      f.name === "languageScoreExpectedOn" &&
                      staticLanguageScoreReceived !== "No";
                    const hideLanguageMarks =
                      (f.name === "languageExamScore" ||
                        f.name === "languageTotalMarks") &&
                      staticLanguageScoreReceived === "No";

                    if (
                      f?.type === "hidden" ||
                      hideCoreExpectedDate ||
                      hideCoreMarks ||
                      hideLanguageExpectedDate ||
                      hideLanguageMarks
                    ) {
                      return null;
                    }

                    return (
                      <Grid item xs={12} sm={3} key={f.id}>
                        {renderField && renderField(f)}
                      </Grid>
                    );
                  })}
                </Grid>
              );
            }
            return null;
          })()}

          {/* Dynamic entries: group by suffix, render each group in a row with remove, with conditional required logic */}
          {(() => {
            const dynamicFields = fields.filter((f: any) => f.isRemoved);
            // Group by suffix: e.g. coreExamName_6 => group 6
            const groupMap: { [suffix: string]: any[] } = {};
            dynamicFields.forEach((f: any) => {
              const match = f.name.match(/_(\d+)$/);
              if (match) {
                const suffix = match[1];
                if (!groupMap[suffix]) groupMap[suffix] = [];
                groupMap[suffix].push(f);
              }
            });
            // Sort groups by suffix number
            const sortedSuffixes = Object.keys(groupMap).sort(
              (a, b) => Number(a) - Number(b)
            );
            return sortedSuffixes.map((suffix, groupIdx) => {
              const group = groupMap[suffix].sort(
                (a, b) => a.fieldOrder - b.fieldOrder
              );
              const dynamicCoreScoreReceived =
                formikRef.current?.values?.[`coreScoreReceived_${suffix}`];
              const dynamicLanguageScoreReceived =
                formikRef.current?.values?.[`languageScoreReceived_${suffix}`];
              // For each group, update required logic for score/marks fields based on corresponding Score Received value
              const updatedGroup = group.map((field: any) => {
                // For core dynamic fields
                if (
                  field.name === `coreExamScore_${suffix}` ||
                  field.name === `coreTotalMarks_${suffix}`
                ) {
                  const scoreReceived =
                    formikRef.current?.values?.[`coreScoreReceived_${suffix}`];
                  if (scoreReceived === "No") {
                    return { ...field, required: false };
                  }
                  if (scoreReceived === "Yes") {
                    return { ...field, required: true };
                  }
                }
                // Make core dynamic scoreExpectedOn required when scoreReceived is No
                if (field.name === `coreScoreExpectedOn_${suffix}`) {
                  const scoreReceived =
                    formikRef.current?.values?.[`coreScoreReceived_${suffix}`];
                  return { ...field, required: scoreReceived === "No" };
                }
                // For language dynamic fields
                if (
                  field.name === `languageExamScore_${suffix}` ||
                  field.name === `languageTotalMarks_${suffix}`
                ) {
                  const scoreReceived =
                    formikRef.current?.values?.[
                      `languageScoreReceived_${suffix}`
                    ];
                  if (scoreReceived === "No") {
                    return { ...field, required: false };
                  }
                  if (scoreReceived === "Yes") {
                    return { ...field, required: true };
                  }
                }
                // Make language dynamic scoreExpectedOn required when scoreReceived is No
                if (field.name === `languageScoreExpectedOn_${suffix}`) {
                  const scoreReceived =
                    formikRef.current?.values?.[
                      `languageScoreReceived_${suffix}`
                    ];
                  return { ...field, required: scoreReceived === "No" };
                }
                return field;
              });
              // Collect all field ids in this group for remove
              const fieldIds = group.map((f: any) => f.id);

              return (
                <React.Fragment key={suffix}>
                  <Grid container item spacing={2} alignItems="center">
                    {updatedGroup.map((f: any) => {
                      const hideCoreExpectedDate =
                        f.name === `coreScoreExpectedOn_${suffix}` &&
                        dynamicCoreScoreReceived !== "No";
                      const hideCoreMarks =
                        (f.name === `coreExamScore_${suffix}` ||
                          f.name === `coreTotalMarks_${suffix}`) &&
                        dynamicCoreScoreReceived === "No";
                      const hideLanguageExpectedDate =
                        f.name === `languageScoreExpectedOn_${suffix}` &&
                        dynamicLanguageScoreReceived !== "No";
                      const hideLanguageMarks =
                        (f.name === `languageExamScore_${suffix}` ||
                          f.name === `languageTotalMarks_${suffix}`) &&
                        dynamicLanguageScoreReceived === "No";

                      if (
                        f?.type === "hidden" ||
                        hideCoreExpectedDate ||
                        hideCoreMarks ||
                        hideLanguageExpectedDate ||
                        hideLanguageMarks
                      ) {
                        return null;
                      }

                      return (
                        <Grid item xs={12} sm={3} key={f.id}>
                          {renderField && renderField(f)}
                        </Grid>
                      );
                    })}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      sx={{ textAlign: "left" }}
                    >
                      <Box
                        sx={{ display: "inline-flex", cursor: "pointer" }}
                        onClick={() => handleRemoveClick(fieldIds)}
                      >
                        <img src={RemoveIcon} alt="arrow-narrow-right" />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            ml: 1,
                            fontWeight: 600,
                            color: "#FF4C51",
                            fontSize: "13px",
                          }}
                        >
                          Remove
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </React.Fragment>
              );
            });
          })()}
        </>
      )}

      {isXsOrSm && showFields && (
        <Grid item xs={12}>
          <Button
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#33c2c4",
              border: "1px solid #33c2c4",
              textAlign: "center",
              width: "100%",
            }}
            onClick={addExamFields}
            disabled={isLoading || disabledAddMoreBtn}
          >
            <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
            {addMoreBtnTitle}
          </Button>
        </Grid>
      )}

      {/* Confirmation Modal */}
      <ModalPopup
        isOpen={confirmOpen}
        onClose={handleCancelRemove}
        subTitle={
          sectionPrefix === "core"
            ? "Remove Core Entrance Exam"
            : sectionPrefix === "language"
            ? "Remove Language Entrance Exam"
            : "Remove Entrance Exam"
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this exam entry? This action cannot
            be undone.
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
          >
            <Button
              onClick={handleCancelRemove}
              color="primary"
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <PrimaryButton onClick={handleConfirmRemove} variant="contained">
              Confirm
            </PrimaryButton>
          </Box>
        </Box>
      </ModalPopup>
    </Grid>
  );
};

export default EntranceExamDetailsSection;